import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { Divider, IconButton, CircularProgress } from '@mui/material';
import fileOutline from '@iconify-icons/eva/file-outline';
import emailOutline from '@iconify-icons/eva/email-outline';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { GridCallbackDetails, GridColumns } from '@mui/x-data-grid';

import { MIconButton } from '../../../../../../../@material-extend';
import MembershipDocumentsModal from '../MembershipDocuments/MBDocumentsModal';
import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';
import { useEmailDocument, useGetWqDocuments } from '@/api/documents/staff';
import { convertToStoreLocalTime } from '@/common/utils/time';
import { DocumentType } from '../index';

interface CustomerData {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  photoUrl?: string;
}

interface POWDocumentsTabProps {
  customerAllData: CustomerData;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  'core-waiver': 'Core Waiver',
  'waiver-signature': 'Core Waiver Signature',
  'mb-agreement': 'MB Agreement',
  'membership-signature': 'Membership Signature',
  'medical-waiver': 'Medical Waiver',
  'legacy-medical-waiver': 'Legacy Medical Waiver'
};

export default function POWDocumentsTab(props: POWDocumentsTabProps) {
  const { customerAllData } = props;

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isMembershipDocumentsModalOpen, setIsMembershipDocumentsModalOpen] =
    useState<boolean | null>(null);
  const [page, setPage] = useState(0);
  const [emailVariables, setEmailVariables] = useState<any>(null);
  const [presignedUrl, setPresignedUrl] = useState<any>('');
  const [selectedVariables, setSelectedVariables] = useState<any>({
    s3Filename: '',
    customer: ''
  });

  const { data: wqData, isLoading: wqDocumentsLoading } = useGetWqDocuments(
    { customerId: customerAllData?.id },
    { query: { enabled: !!customerAllData?.id } }
  );

  const tableData = wqData?.data?.documents ?? [];

  const { mutate: sendEmail, isPending: sendingEmail } = useEmailDocument({
    mutation: {
      onSuccess: response => {
        if (response?.data?.success) {
          enqueueSnackbar(
            'Document copy emailed to ' +
              customerAllData?.firstName +
              ' ' +
              customerAllData?.lastName,
            {
              variant: 'success',
              action: (key: any) => (
                <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                  <CloseIcon />
                </MIconButton>
              )
            }
          );
        } else {
          enqueueSnackbar(
            'Failed to send document copy to ' +
              customerAllData?.firstName +
              ' ' +
              customerAllData?.lastName,
            { variant: 'error' }
          );
        }
      },
      onError: () => {
        enqueueSnackbar(
          'Failed to send document copy to ' +
            customerAllData?.firstName +
            ' ' +
            customerAllData?.lastName,
          { variant: 'error' }
        );
      }
    }
  });

  React.useEffect(() => {
    if (selectedVariables?.s3Filename) {
      setPresignedUrl(selectedVariables?.s3Filename);
    }
  }, [selectedVariables]);

  React.useEffect(() => {
    if (emailVariables !== null) {
      sendEmail({ data: emailVariables });
    }
  }, [emailVariables]);

  const columnConfig: GridColumns = [
    {
      field: 'type',
      headerName: 'Description',
      flex: 3,
      sortable: false,
      renderCell: ({ row }) => DOCUMENT_TYPE_LABELS[row.type] ?? row.type
    },
    {
      field: 'signedAt',
      headerName: 'Sign Date',
      flex: 2,
      sortable: false,
      renderCell: ({ row }) =>
        row.signedAt ? convertToStoreLocalTime(row.signedAt, 'YYYY-MM-DD') : ''
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.8,
      sortable: false,
      renderCell: ({ row }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly'
          }}
        >
          <IconButton
            onClick={() => {
              setSelectedDocument(row);
              setIsMembershipDocumentsModalOpen(true);
              setSelectedVariables({
                s3Filename: row.viewUrl,
                customer: customerAllData?.id
              });
            }}
            size="small"
          >
            <Icon icon={fileOutline} color="#055577" />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          {sendingEmail && emailVariables?.s3Filename === row.pdfFilename ? (
            <CircularProgress size={'2em'} />
          ) : (
            <IconButton
              disabled={sendingEmail}
              size="small"
              onClick={() => {
                setEmailVariables({
                  s3Filename: row.pdfFilename,
                  customerId: customerAllData?.id,
                  documentType: row.type === 'waiver-signature' ? DocumentType.CORE_WAIVER : DocumentType.MEMBERSHIP
                });
              }}
            >
              <Icon icon={emailOutline} color="#055577" />
            </IconButton>
          )}
        </div>
      )
    }
  ];

  const rows = tableData
    .map((row: any) => ({ ...row, id: row.guid }))
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const onPageSizeChange = (pageSize: number, _details: GridCallbackDetails) => {
    setRowsPerPage(pageSize);
    setPage(0);
  };
  return (
    <>
      <RestoreDataGrid
        rowCount={tableData.length}
        columnConfig={columnConfig}
        rows={rows}
        loading={wqDocumentsLoading}
        page={page}
        pageSize={rowsPerPage}
        pageSizes={[10, 50, 100]}
        sortModel={[]}
        emptyListMessage={`No documents found for ${customerAllData?.firstName} ${customerAllData?.lastName}`}
        onPageChange={setPage}
        onPageChangeSize={onPageSizeChange}
        onSortModelChange={() => {}}
      />
      {selectedDocument && (
        <MembershipDocumentsModal
          presignedURL={presignedUrl}
          showPrintButton={true}
          showEmailButton={false}
          selectedDocument={selectedDocument}
          open={isMembershipDocumentsModalOpen}
          setModal={setIsMembershipDocumentsModalOpen}
          setEmailVariables={setEmailVariables}
          selectedVariables={selectedVariables}
          sendingEmail={sendingEmail}
        />
      )}
    </>
  );
}
